import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { difference } from 'ramda';
import { calculateDBSEffectFromDocuments, calculateLevodopaTestFromDocuments } from 'neuro-calculation-commons';
import { isPartialDate, exists, arrayOfAll } from 'neuro-utils';
import { sortAndTakeFirst } from '../utils';
import type { TRelevantDocumentType } from '../utils';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { sortDocuments } from 'Utility/randomUtil';
import colors from '../../../config/theme/colors';
import { updrs_iii, ftmtrs } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import { mapDocumentToNeuroDocument } from 'Utility/documentHandling';
import { fm } from 'Components/FormatMessage';
import type { IData } from 'Components/DashboardTile/TileContentMaker/components/definitions';

const {
  calculators: { calculateFTMTRSScore },
} = ftmtrs;

type TUpdrsDashDocumentType = Exclude<TRelevantDocumentType, 'dbs'>;

const updrsDashDocumentTypes = arrayOfAll.options<TUpdrsDashDocumentType>()([
  'updrs_iii',
  'updrs_v',
  'ftmtrs',
  'levodopatest',
  'dbsEffect',
]);

const getUpdrsIIIValue = (d: IDocumentWithTypeV2<'updrs_iii'>) => {
  if (d.testType === 'UPDRS' && d.retrospectiveData?.[0] === true) {
    const manualScore = d.manualScore;
    return manualScore || manualScore === 0 ? `${manualScore}` : '';
  }
  const score = updrs_iii.calculateScore(d);
  return score || score === 0 ? `${score}` : '';
};

const getFtmtrsValue = (d: IDocumentWithTypeV2<'ftmtrs'>, fm: (s: string) => string) => {
  const whichHand = d.whichHand;

  const aScore = calculateFTMTRSScore<'a'>(d, 'a');
  const bScore = calculateFTMTRSScore<'b'>(d, 'b');
  const cScore = calculateFTMTRSScore<'c'>(d, 'c');

  const bScoreRight = whichHand === 'both' || whichHand === 'right' ? (bScore?.right ?? null) : null;
  const bScoreLeft = whichHand === 'both' || whichHand === 'left' ? (bScore?.left ?? null) : null;

  const rightString = bScoreRight || bScoreRight === 0 ? fm('updrs.rightShort') + bScoreRight : null;
  const leftString = bScoreLeft || bScoreLeft === 0 ? fm('updrs.leftShort') + bScoreLeft : null;

  return `${aScore ?? '-'} / ${rightString ?? ''} ${leftString ?? ''} / ${cScore ?? '-'}`;
};

const getLevodopaValue = (d: IDocumentWithTypeV2<'levodopatest'>, allDocuments: INeuroDocWithData[]): string => {
  if (exists(d.manualScore)) return `${d.manualScore}%`;

  const neuroDoc = mapDocumentToNeuroDocument(d);
  const calculated = calculateLevodopaTestFromDocuments(neuroDoc, allDocuments);

  return typeof calculated === 'number' ? `${Math.round(calculated * 100)}%` : '0%';
};

const getDBSValue = (d: IDocumentWithTypeV2<'dbsEffect'>, allDocuments: INeuroDocWithData[]): string => {
  if (exists(d.manualScore)) return `${d.manualScore}%`;

  const neuroDoc = mapDocumentToNeuroDocument(d);
  const calculated = calculateDBSEffectFromDocuments(neuroDoc, allDocuments);

  return typeof calculated === 'number' ? `${Math.round(calculated * 100)}%` : '0%';
};

const UpdrsDash = ({ documents }: IOwnProps): React.JSX.Element => {
  // 1. Prepare data
  const sortedDocs = updrsDashDocumentTypes
    .map(type => sortAndTakeFirst(type, documents))
    .filter(d => d !== undefined)
    .sort((n1, n2) => sortDocuments([{ type: 'date', sortField: 'date' }])(n1, n2));

  const updrsMissingTypes = difference(
    updrsDashDocumentTypes,
    sortedDocs.map(d => d._type),
  );

  const { formatMessage } = useIntl();

  const updrsTypesDisplayed = (type: TUpdrsDashDocumentType) =>
    ({
      updrs_v: formatMessage({ id: 'updrs.updrsV' }),
      updrs_iii: formatMessage({ id: 'updrs.updrsIII' }),
      ftmtrs: formatMessage({ id: 'updrs.ftmtrsShort' }),
      levodopatest: formatMessage({ id: 'updrs.levodopatest' }),
      dbsEffect: formatMessage({ id: 'updrs.dbsEffect' }),
    })[type];

  // 2. Prepare context for calculation
  const neuroAllDocuments = React.useMemo(() => documents.map(mapDocumentToNeuroDocument), [documents]);

  return (
    <>
      <TileContentMaker
        type="specificList"
        data={sortedDocs.map((d): IData => {
          const date = isPartialDate(d.date) ? formatPartialDate(d.date) : '';
          switch (d._type) {
            case 'updrs_v':
              return { date, title: fm('updrs.updrsV'), value: d.value, key: 'updrs_v' };
            case 'updrs_iii':
              return {
                date,
                title: d.testType === 'UPDRS' ? fm('updrs.updrsIII') : fm('updrs.mds'),
                value: getUpdrsIIIValue(d),
                key: 'updrsIII',
              };
            case 'ftmtrs':
              return {
                date,
                title: fm('updrs.ftmtrsShort'),
                value: getFtmtrsValue(d, id => formatMessage({ id })),
                key: 'ftmtrs',
              };
            case 'levodopatest':
              return {
                date,
                title: fm('updrs.levodopatest'),
                value: getLevodopaValue(d, neuroAllDocuments),
                key: 'levodopatest',
              };
            case 'dbsEffect':
              return { date, title: fm('updrs.dbs'), value: getDBSValue(d, neuroAllDocuments), key: 'dbsEffect' };
          }
        })}
      />
      {updrsMissingTypes.length > 0 ? (
        <div style={{ color: colors.darkGray, fontSize: '1.4rem', marginTop: '0.2rem' }}>
          <FormattedMessage id={updrsMissingTypes.length === 4 ? 'general.theseMeters' : 'general.alsoTheseMeters'} />:{' '}
          {updrsMissingTypes.map(type => updrsTypesDisplayed(type)).join(', ')}
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<TUpdrsDashDocumentType>[];
}

export default UpdrsDash;
